import {
  FrameShape,
  getArcParams,
  InnerBeam,
  SideWidths,
} from '@cutr/constants/windows/windows-geometry.ts';
import { Group, Path } from 'react-konva';

import { buildArcTopPath, makeArcTopClipFunc } from '../arcTopPath';
import { position } from '../colors';
import { DEFAULT_FRAME_BEAM_WIDTHS } from '../dimensions';
import { InnerGrid } from '../InnerGrid';
import { useKonva } from '../KonvaContext';

type FrameProps = {
  frame: FrameShape;
  x: number;
  y: number;
  innerBeams: InnerBeam[];
  frameBeamWidthsMM?: SideWidths;
};

export const ArcTopFrame = ({
  frame,
  x,
  y,
  innerBeams,
  frameBeamWidthsMM = DEFAULT_FRAME_BEAM_WIDTHS,
}: FrameProps) => {
  const { renderStyles } = useKonva();
  if (frame.frameType !== 'rectangle-arc-top') return null;

  const { widthMM, heightMM } = frame;
  const { top, right, bottom, left } = frameBeamWidthsMM;
  const { cx, cy, r } = getArcParams(frame);
  const topEdge = { cx, cy, r };

  const outerData = buildArcTopPath({
    xLeft: 0,
    xRight: widthMM,
    yBottom: 0,
    yTop: heightMM,
    topEdge,
  });

  // The top edge is the arc, so the top beam insets the arc radius; the
  // straight left/right/bottom edges inset independently.
  const innerData = buildArcTopPath({
    xLeft: left,
    xRight: widthMM - right,
    yBottom: bottom,
    yTop: heightMM,
    topEdge,
    arcInset: top,
  });

  const clipInnerGlass = makeArcTopClipFunc({
    box: {
      x: left,
      y: bottom,
      width: widthMM - left - right,
      height: heightMM - bottom,
    },
    topEdge: { cx, cy, r: r - top },
  });

  return (
    <Group x={x} y={y}>
      <Path
        data={outerData}
        fill={position.frameFill}
        stroke={position.frameStroke}
        strokeWidth={renderStyles.frameStrokeWidthMM}
      />
      <Group clipFunc={clipInnerGlass}>
        <Path
          data={innerData}
          fill={position.glass}
          stroke={position.frameStroke}
          strokeWidth={renderStyles.frameStrokeWidthMM}
        />
        <InnerGrid innerBeams={innerBeams} />
      </Group>
    </Group>
  );
};
